import type { StatusTone } from '../../StatusBadge.js';
import type {
  AwardProposalInput,
  AwardProposalLineInput,
  AwardStatus,
  AwardSubstitute,
  AwardTerms,
  RfqAward,
  RfqAwards,
  RfqDetail,
  RfqQuotation,
} from './types.js';

/**
 * RFQ ▸ Award ekranının saf mantığı (ADR 0111 §D): satır başına seçenekler (teklif veren
 * tedarikçinin fiyatlı ya da ikame yanıtı), varsayılan bölme (satırın en iyi fiyatı), tedarikçi
 * başına öneri gövdesi ve durum tonları. Sunucu aynı kuralları yeniden denetler.
 */

export interface AwardOption {
  key: string;
  supplierId: string;
  supplierName: string;
  kind: 'priced' | 'substitute';
  quantity: number;
  unitPrice: number;
  unit: string;
  substitute: AwardSubstitute | null;
  /** Satırın en iyi fiyatı (satır karşılaştırması). */
  best: boolean;
}

export interface AwardDraftRow {
  /** Seçilen seçenek (`tedarikçi:tür`); boş: bu satır önerilmez. */
  optionKey: string | null;
  quantity: number | null;
  unitPrice: number | null;
}

export type AwardDraft = Record<string, AwardDraftRow>;

const optionKey = (supplierId: string, kind: AwardOption['kind']) => `${supplierId}:${kind}`;

/** Ödül hâlâ değişebilir: kesinleşmiş ya da iptal edilmiş değil. */
export const liveAward = (award: RfqAward) =>
  award.status !== 'finalized' && award.status !== 'cancelled' && award.status !== 'draft';

/** Satırın seçenekleri: fiyatlı ve ikame yanıtlar; en iyi fiyat önce, sonra fiyata göre. */
export function lineOptions(detail: RfqDetail, rfqLineId: string): AwardOption[] {
  const line = detail.lines.find((l) => l.id === rfqLineId);
  const row = detail.lineComparison?.lines.find((l) => l.rfqLineId === rfqLineId);
  const options: AwardOption[] = [];
  for (const quotation of detail.quotations) {
    const answer = quotation.lines.find((l) => l.rfqLineId === rfqLineId);
    if (!answer || !line) continue;
    const response = answer.response ?? (answer.unitPrice === null ? 'no_price' : 'priced');
    if (response === 'priced' && answer.unitPrice !== null) {
      options.push({
        key: optionKey(quotation.supplierId, 'priced'),
        supplierId: quotation.supplierId,
        supplierName: quotation.supplierName,
        kind: 'priced',
        quantity: line.quantity,
        unitPrice: answer.unitPrice,
        unit: line.unit,
        substitute: null,
        best: row?.bestQuotationIds.includes(quotation.id) ?? false,
      });
    } else if (response === 'substitute' && answer.substitute) {
      const sub = answer.substitute;
      options.push({
        key: optionKey(quotation.supplierId, 'substitute'),
        supplierId: quotation.supplierId,
        supplierName: quotation.supplierName,
        kind: 'substitute',
        quantity: sub.quantity ?? line.quantity,
        unitPrice: sub.unitPrice ?? 0,
        unit: sub.unit,
        substitute: { name: sub.name, specification: sub.specification, unit: sub.unit },
        best: false,
      });
    }
  }
  return options.sort(
    (a, b) =>
      Number(b.best) - Number(a.best) ||
      Number(a.kind === 'substitute') - Number(b.kind === 'substitute') ||
      a.unitPrice - b.unitPrice,
  );
}

/** Tedarikçinin kesinleşmiş ödülü var mı (RFQ'da tedarikçi başına tek etkin ödül). */
export function supplierLocked(awards: RfqAwards, supplierId: string): boolean {
  return awards.awards.some((a) => a.supplierId === supplierId && a.status === 'finalized');
}

/** Varsayılan bölme: boştaki her satıra satırın en iyi fiyatı (yoksa ilk seçenek). */
export function initialDraft(detail: RfqDetail, awards: RfqAwards): AwardDraft {
  const draft: AwardDraft = {};
  for (const slot of awards.lines) {
    if (slot.awardId !== null) continue;
    const option = lineOptions(detail, slot.rfqLineId).find(
      (o) => !supplierLocked(awards, o.supplierId),
    );
    draft[slot.rfqLineId] = option
      ? { optionKey: option.key, quantity: option.quantity, unitPrice: option.unitPrice }
      : { optionKey: null, quantity: null, unitPrice: null };
  }
  return draft;
}

/** Tekliften varsayılan koşullar (teslim süresi, masraflar) ve RFQ'nun teslim yeri ve ödemesi. */
export function defaultTerms(detail: RfqDetail, quotation: RfqQuotation | undefined): AwardTerms {
  return {
    leadTimeDays: quotation?.leadTimeDays ?? null,
    deliveryLocation: detail.deliveryLocation,
    deliveryTerms: null,
    paymentTerms: detail.paymentTerms,
    warranty: quotation?.warranty ?? detail.warranty,
    discount: 0,
    freight: quotation?.freight ?? 0,
    customs: quotation?.customs ?? 0,
    tax: quotation?.tax ?? 0,
    serviceFee: quotation?.serviceFee ?? 0,
    remarks: null,
  };
}

const roundMoney = (value: number) => Math.round(value * 1e6) / 1e6;

export interface SupplierProposal {
  supplierId: string;
  supplierName: string;
  /** Tedarikçinin etkin ödülü: öneri yeni tur olarak ona gider (satırlar birleşir). */
  award: RfqAward | null;
  /** Bölmede seçilen yeni satır sayısı. */
  added: number;
  proposal: AwardProposalInput;
  itemsTotal: number;
}

/**
 * Bölmeden tedarikçi başına öneri: seçilen satırlar; tedarikçinin etkin ödülü varsa onun geçerli
 * satırlarıyla birleşir ve koşulları korunur. Miktarı ya da fiyatı eksik satır öneriye girmez.
 */
export function draftProposals(
  detail: RfqDetail,
  awards: RfqAwards,
  draft: AwardDraft,
  message: string | null = null,
): SupplierProposal[] {
  const bySupplier = new Map<string, SupplierProposal>();
  for (const [rfqLineId, row] of Object.entries(draft)) {
    if (!row.optionKey || row.quantity === null || row.unitPrice === null) continue;
    if (!(row.quantity > 0) || !(row.unitPrice >= 0)) continue;
    const option = lineOptions(detail, rfqLineId).find((o) => o.key === row.optionKey);
    if (!option) continue;
    let entry = bySupplier.get(option.supplierId);
    if (!entry) {
      const award =
        awards.awards.find((a) => a.supplierId === option.supplierId && liveAward(a)) ?? null;
      const quotation = detail.quotations.find((q) => q.supplierId === option.supplierId);
      const kept: AwardProposalLineInput[] = (award?.lines ?? []).map((line) => ({
        rfqLineId: line.rfqLineId,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        substitute: line.substitute,
        note: line.note,
      }));
      entry = {
        supplierId: option.supplierId,
        supplierName: option.supplierName,
        award,
        added: 0,
        proposal: {
          lines: kept,
          terms: award?.terms ?? defaultTerms(detail, quotation),
          message,
        },
        itemsTotal: 0,
      };
      bySupplier.set(option.supplierId, entry);
    }
    entry.added += 1;
    entry.proposal.lines.push({
      rfqLineId,
      quantity: row.quantity,
      unitPrice: row.unitPrice,
      substitute: option.substitute,
      note: null,
    });
  }
  const lineNo = new Map(detail.lines.map((line) => [line.id, line.lineNo]));
  return [...bySupplier.values()].map((entry) => {
    entry.proposal.lines.sort(
      (a, b) => (lineNo.get(a.rfqLineId) ?? 0) - (lineNo.get(b.rfqLineId) ?? 0),
    );
    entry.itemsTotal = roundMoney(
      entry.proposal.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0),
    );
    return entry;
  });
}

/** Ödül durumunun rozet tonu. */
export function awardTone(status: AwardStatus): StatusTone {
  switch (status) {
    case 'proposed':
      return 'info';
    case 'countered':
      return 'warning';
    case 'accepted':
    case 'finalized':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** "100 → 150" (değer biçimi çağırandan). */
export function changeText<T>(
  change: { from: T | null; to: T | null },
  format: (value: T) => string,
): string {
  const side = (value: T | null) => (value === null ? '—' : format(value));
  return `${side(change.from)} → ${side(change.to)}`;
}
